import type { BlogArticle } from '../../types/blog'
import { BLOG_DATE_MODIFIED, BLOG_DATE_PUBLISHED } from './blogCommon'

export const veneersGuideArticle: BlogArticle = {
  slug: 'veneers-london-everything-you-need-to-know',
  theme: 'teal',
  datePublished: BLOG_DATE_PUBLISHED,
  dateModified: BLOG_DATE_MODIFIED,
  category: { en: 'Aesthetics', ua: 'Естетика', ru: 'Эстетика', ro: 'Estetică' },
  readTime: { en: '9 min read', ua: '9 хв читання', ru: '9 мин чтения', ro: '9 min citire' },
  seoTitle: {
    en: 'Veneers in London: Types, Prices and What to Know | Dentico Clinic',
    ua: 'Вініри в Лондоні: види, ціни і що важливо знати | Dentico Clinic',
    ru: 'Виниры в Лондоне: виды, цены и что важно знать | Dentico Clinic',
    ro: 'Fațete dentare în Londra: tipuri, prețuri și ce trebuie să știți | Dentico Clinic',
  },
  seoDescription: {
    en: 'A patient-friendly guide to veneers in London, including ceramic veneers, composite bonding, treatment planning and what affects the final cost.',
    ua: 'Зрозумілий гід про вініри в Лондоні: керамічні вініри, композитне відновлення, планування лікування і що впливає на остаточну вартість.',
    ru: 'Понятный гид о винирах в Лондоне: керамические виниры, композитная реставрация, планирование лечения и что влияет на итоговую стоимость.',
    ro: 'Un ghid pe înțeles despre fațetele dentare în Londra: fațete ceramice, bonding compozit, planificarea tratamentului și ce influențează costul final.',
  },
  h1: {
    en: 'Veneers in London: types, prices and what to know',
    ua: 'Вініри в Лондоні: види, ціни і що важливо знати',
    ru: 'Виниры в Лондоне: виды, цены и что важно знать',
    ro: 'Fațete dentare în Londra: tipuri, prețuri și ce trebuie să știți',
  },
  cardTitle: {
    en: 'Veneers in London: types, prices and what to know',
    ua: 'Вініри в Лондоні: види, ціни і що знати',
    ru: 'Виниры в Лондоне: виды, цены и что знать',
    ro: 'Fațete în Londra: tipuri, prețuri și ce să știți',
  },
  cardExcerpt: {
    en: 'Ceramic veneers vs composite bonding, how treatment is planned, what affects the cost and how to care for your new smile.',
    ua: 'Керамічні вініри чи композит, як планується лікування, що впливає на вартість і як доглядати за новою усмішкою.',
    ru: 'Керамические виниры или композит, как планируется лечение, что влияет на стоимость и как ухаживать за новой улыбкой.',
    ro: 'Fațete ceramice vs bonding compozit, cum se planifică tratamentul, ce influențează costul și cum îngrijiți noul zâmbet.',
  },
  intro: {
    en: 'Veneers can improve the shape, colour and alignment of front teeth, but they are not the right choice for everyone. Some patients get a better result from whitening, composite bonding or orthodontics first. This guide explains the main types of veneers, how treatment is planned, what affects the price in London and what to ask before you decide.',
    ua: 'Вініри можуть покращити форму, колір і положення передніх зубів, але підходять не всім. Деяким пацієнтам кращий результат дає відбілювання, композитне відновлення або спершу ортодонтія. У цьому гіді пояснюємо основні види вінірів, як планується лікування, що впливає на ціну в Лондоні і що запитати перед рішенням.',
    ru: 'Виниры могут улучшить форму, цвет и положение передних зубов, но подходят не всем. Некоторым пациентам лучший результат даёт отбеливание, композитная реставрация или сначала ортодонтия. В этом гиде объясняем основные виды виниров, как планируется лечение, что влияет на цену в Лондоне и что спросить перед решением.',
    ro: 'Fațetele pot îmbunătăți forma, culoarea și alinierea dinților frontali, dar nu sunt potrivite pentru oricine. Unii pacienți obțin un rezultat mai bun cu albire, bonding compozit sau mai întâi ortodonție. Acest ghid explică principalele tipuri de fațete, cum se planifică tratamentul, ce influențează prețul în Londra și ce să întrebați înainte de a decide.',
  },
  sections: [
    {
      heading: {
        en: 'What veneers are and who they suit',
        ua: 'Що таке вініри і кому вони підходять',
        ru: 'Что такое виниры и кому они подходят',
        ro: 'Ce sunt fațetele și cui se potrivesc',
      },
      blocks: [
        {
          type: 'p',
          text: {
            en: 'A veneer is a thin shell of ceramic or composite bonded to the front of a tooth. Veneers are often considered for chipped or worn edges, gaps, uneven shapes or discolouration that does not respond to whitening. Healthy gums and a stable bite are important, and teeth grinding needs to be managed to protect the result.',
            ua: 'Вінір — це тонка керамічна або композитна накладка, яку фіксують на передню поверхню зуба. Вініри розглядають при сколах чи стертих краях, щілинах, нерівній формі або потемнінні, яке не піддається відбілюванню. Важливі здорові ясна і стабільний прикус, а бруксизм потрібно контролювати, щоб зберегти результат.',
            ru: 'Винир — это тонкая керамическая или композитная накладка, которую фиксируют на переднюю поверхность зуба. Виниры рассматривают при сколах или стёртых краях, щелях, неровной форме или потемнении, которое не поддаётся отбеливанию. Важны здоровые дёсны и стабильный прикус, а бруксизм нужно контролировать, чтобы сохранить результат.',
            ro: 'Fațeta este un strat subțire din ceramică sau compozit, lipit pe fața dintelui. Fațetele sunt luate în considerare pentru margini ciobite sau uzate, spații, forme neregulate sau colorații care nu răspund la albire. Gingiile sănătoase și o ocluzie stabilă sunt importante, iar bruxismul trebuie controlat pentru a proteja rezultatul.',
          },
        },
      ],
    },
    {
      heading: {
        en: 'Ceramic veneers vs composite bonding',
        ua: 'Керамічні вініри чи композитне відновлення',
        ru: 'Керамические виниры или композитная реставрация',
        ro: 'Fațete ceramice vs bonding compozit',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              title: { en: 'Ceramic (porcelain) veneers:', ua: 'Керамічні вініри:', ru: 'Керамические виниры:', ro: 'Fațete ceramice (porțelan):' },
              text: {
                en: 'made in a dental laboratory, very stain-resistant and natural-looking. They usually need some preparation of the tooth surface and two or more visits.',
                ua: 'виготовляються в зуботехнічній лабораторії, дуже стійкі до пігментації і природні на вигляд. Зазвичай потребують певного препарування поверхні зуба і двох або більше візитів.',
                ru: 'изготавливаются в зуботехнической лаборатории, очень устойчивы к пигментации и естественны на вид. Обычно требуют некоторой обработки поверхности зуба и двух или более визитов.',
                ro: 'realizate în laborator, foarte rezistente la pete și cu aspect natural. De obicei necesită o pregătire a suprafeței dintelui și două sau mai multe vizite.',
              },
            },
            {
              title: { en: 'Composite veneers and bonding:', ua: 'Композитні вініри та відновлення:', ru: 'Композитные виниры и реставрация:', ro: 'Fațete și bonding compozit:' },
              text: {
                en: 'built up directly on the tooth, often in one visit and with little or no drilling. They cost less but may need polishing or repair over time. Composite bonding is part of our [smile makeover treatments](/services/smile-makeover).',
                ua: 'моделюються безпосередньо на зубі, часто за один візит і з мінімальним препаруванням або без нього. Коштують менше, але з часом можуть потребувати полірування чи корекції. Композитне відновлення входить до наших [процедур зі зміни усмішки](/services/smile-makeover).',
                ru: 'моделируются непосредственно на зубе, часто за один визит и с минимальной обработкой или без неё. Стоят меньше, но со временем могут требовать полировки или коррекции. Композитная реставрация входит в наши [процедуры по изменению улыбки](/services/smile-makeover).',
                ro: 'modelate direct pe dinte, adesea într-o singură vizită și cu șlefuire minimă sau deloc. Costă mai puțin, dar pot necesita lustruire sau reparații în timp. Bondingul compozit face parte din [tratamentele noastre de smile makeover](/services/smile-makeover).',
              },
            },
            {
              title: { en: 'Whitening first:', ua: 'Спершу відбілювання:', ru: 'Сначала отбеливание:', ro: 'Mai întâi albire:' },
              text: {
                en: 'veneers do not change colour after fitting, so many patients have [teeth whitening](/services/whitening) beforehand to set the shade of the surrounding teeth.',
                ua: 'вініри не змінюють колір після фіксації, тому багато пацієнтів спершу роблять [відбілювання зубів](/services/whitening), щоб визначити відтінок сусідніх зубів.',
                ru: 'виниры не меняют цвет после фиксации, поэтому многие пациенты сначала делают [отбеливание зубов](/services/whitening), чтобы определить оттенок соседних зубов.',
                ro: 'fațetele nu își schimbă culoarea după montare, așa că mulți pacienți fac mai întâi [albire dentară](/services/whitening) pentru a stabili nuanța dinților din jur.',
              },
            },
          ],
        },
      ],
    },
    {
      heading: {
        en: 'How veneer treatment is planned',
        ua: 'Як планується лікування вінірами',
        ru: 'Как планируется лечение винирами',
        ro: 'Cum se planifică tratamentul cu fațete',
      },
      blocks: [
        {
          type: 'steps',
          items: [
            {
              title: { en: 'Consultation and assessment', ua: 'Консультація та огляд', ru: 'Консультация и осмотр', ro: 'Consultație și evaluare' },
              text: {
                en: 'The dentist checks gum health, bite and existing restorations, and discusses what you would like to change.',
                ua: 'Лікар оцінює стан ясен, прикус і наявні реставрації та обговорює, що ви хочете змінити.',
                ru: 'Врач оценивает состояние дёсен, прикус и имеющиеся реставрации и обсуждает, что вы хотите изменить.',
                ro: 'Medicul verifică sănătatea gingiilor, ocluzia și restaurările existente și discută ce doriți să schimbați.',
              },
            },
            {
              title: { en: 'Smile design and preview', ua: 'Дизайн усмішки та попередній перегляд', ru: 'Дизайн улыбки и предварительный просмотр', ro: 'Design al zâmbetului și previzualizare' },
              text: {
                en: 'Photos, scans and sometimes a trial mock-up help you see the planned shape before any permanent work.',
                ua: 'Фото, сканування і часом пробний макет допомагають побачити заплановану форму ще до постійної роботи.',
                ru: 'Фото, сканирование и иногда пробный макет помогают увидеть запланированную форму ещё до постоянной работы.',
                ro: 'Fotografiile, scanările și uneori o machetă de probă vă ajută să vedeți forma planificată înaintea lucrării definitive.',
              },
            },
            {
              title: { en: 'Preparation and fitting', ua: 'Підготовка і фіксація', ru: 'Подготовка и фиксация', ro: 'Pregătire și montare' },
              text: {
                en: 'Composite is placed and shaped in the chair; ceramic veneers are made in a lab and bonded at a later visit.',
                ua: 'Композит наносять і моделюють у кріслі; керамічні вініри виготовляють у лабораторії та фіксують на наступному візиті.',
                ru: 'Композит наносят и моделируют в кресле; керамические виниры изготавливают в лаборатории и фиксируют на следующем визите.',
                ro: 'Compozitul se aplică și se modelează pe loc; fațetele ceramice se realizează în laborator și se lipesc la o vizită ulterioară.',
              },
            },
          ],
        },
      ],
    },
    {
      heading: {
        en: 'What affects the cost of veneers in London',
        ua: 'Що впливає на вартість вінірів у Лондоні',
        ru: 'Что влияет на стоимость виниров в Лондоне',
        ro: 'Ce influențează costul fațetelor în Londra',
      },
      blocks: [
        {
          type: 'p',
          text: {
            en: 'Veneers are priced per tooth. As a general guide, composite veneers in London often start from around £250–£400 per tooth, and ceramic veneers from around £700–£1,000+ per tooth. The final cost depends on the material, the number of teeth, any preparatory treatment such as whitening or gum care, and the complexity of the smile design. Dentico Clinic fees are listed on our [prices page](/prices).',
            ua: 'Вартість вінірів розраховується за зуб. Орієнтовно, композитні вініри в Лондоні часто коштують від £250–£400 за зуб, а керамічні — від £700–£1,000+ за зуб. Остаточна ціна залежить від матеріалу, кількості зубів, підготовчого лікування (відбілювання, лікування ясен) і складності дизайну усмішки. Ціни Dentico Clinic — на нашій [сторінці цін](/prices).',
            ru: 'Стоимость виниров рассчитывается за зуб. Ориентировочно, композитные виниры в Лондоне часто стоят от £250–£400 за зуб, а керамические — от £700–£1,000+ за зуб. Итоговая цена зависит от материала, количества зубов, подготовительного лечения (отбеливание, лечение дёсен) и сложности дизайна улыбки. Цены Dentico Clinic — на нашей [странице цен](/prices).',
            ro: 'Fațetele se taxează per dinte. Orientativ, fațetele din compozit în Londra pornesc adesea de la aproximativ £250–£400 pe dinte, iar cele ceramice de la aproximativ £700–£1,000+ pe dinte. Costul final depinde de material, numărul de dinți, tratamentele pregătitoare precum albirea sau îngrijirea gingiilor și complexitatea designului. Tarifele Dentico Clinic sunt pe [pagina de prețuri](/prices).',
          },
        },
      ],
    },
    {
      heading: {
        en: 'Veneers at Dentico Clinic',
        ua: 'Вініри в Dentico Clinic',
        ru: 'Виниры в Dentico Clinic',
        ro: 'Fațete la Dentico Clinic',
      },
      blocks: [
        {
          type: 'p',
          text: {
            en: 'We will always discuss less invasive options first and only recommend veneers where they suit your teeth and goals. Learn more on our [veneers page](/veneers-london), or book a consultation to talk through your options in English, Ukrainian, Russian or Romanian.',
            ua: 'Ми завжди спершу обговорюємо менш інвазивні варіанти і рекомендуємо вініри лише тоді, коли вони підходять вашим зубам і цілям. Детальніше — на нашій [сторінці про вініри](/veneers-london), або запишіться на консультацію українською, англійською, російською чи румунською.',
            ru: 'Мы всегда сначала обсуждаем менее инвазивные варианты и рекомендуем виниры только тогда, когда они подходят вашим зубам и целям. Подробнее — на нашей [странице о винирах](/veneers-london), или запишитесь на консультацию на русском, английском, украинском или румынском.',
            ro: 'Discutăm întotdeauna mai întâi opțiunile mai puțin invazive și recomandăm fațete doar când se potrivesc dinților și obiectivelor dumneavoastră. Aflați mai multe pe [pagina despre fațete](/veneers-london) sau programați o consultație în română, engleză, ucraineană ori rusă.',
          },
        },
      ],
    },
  ],
  faqs: [
    {
      q: { en: 'How long do veneers last?', ua: 'Скільки служать вініри?', ru: 'Сколько служат виниры?', ro: 'Cât durează fațetele?' },
      a: {
        en: 'With good care, ceramic veneers commonly last 10–15 years and composite veneers around 5–7 years, though this varies between patients.',
        ua: 'За належного догляду керамічні вініри зазвичай служать 10–15 років, композитні — близько 5–7 років, хоча це індивідуально.',
        ru: 'При хорошем уходе керамические виниры обычно служат 10–15 лет, композитные — около 5–7 лет, хотя это индивидуально.',
        ro: 'Cu o îngrijire bună, fațetele ceramice durează de obicei 10–15 ani, iar cele din compozit aproximativ 5–7 ani, deși variază de la pacient la pacient.',
      },
    },
    {
      q: { en: 'Do veneers damage natural teeth?', ua: 'Чи шкодять вініри власним зубам?', ru: 'Вредят ли виниры собственным зубам?', ro: 'Deteriorează fațetele dinții naturali?' },
      a: {
        en: 'Ceramic veneers usually require removing a thin layer of enamel, which is irreversible. Composite options often need little or no preparation.',
        ua: 'Керамічні вініри зазвичай потребують зняття тонкого шару емалі, що є незворотним. Композитні варіанти часто потребують мінімального препарування або зовсім без нього.',
        ru: 'Керамические виниры обычно требуют снятия тонкого слоя эмали, что необратимо. Композитные варианты часто требуют минимальной обработки или вовсе без неё.',
        ro: 'Fațetele ceramice necesită de obicei îndepărtarea unui strat subțire de smalț, ceea ce este ireversibil. Variantele din compozit necesită adesea pregătire minimă sau deloc.',
      },
    },
    {
      q: { en: 'Can veneers be whitened?', ua: 'Чи можна відбілити вініри?', ru: 'Можно ли отбелить виниры?', ro: 'Pot fi albite fațetele?' },
      a: {
        en: 'No. Whitening does not change the colour of veneers, which is why whitening is usually done before treatment.',
        ua: 'Ні. Відбілювання не змінює колір вінірів, тому його зазвичай роблять до лікування.',
        ru: 'Нет. Отбеливание не меняет цвет виниров, поэтому его обычно делают до лечения.',
        ro: 'Nu. Albirea nu schimbă culoarea fațetelor, de aceea se face de obicei înainte de tratament.',
      },
    },
    {
      q: { en: 'Is composite bonding an alternative to veneers?', ua: 'Чи є композитне відновлення альтернативою вінірам?', ru: 'Является ли композитная реставрация альтернативой винирам?', ro: 'Este bondingul compozit o alternativă la fațete?' },
      a: {
        en: 'Often, yes, for small chips, gaps or shape changes. Your dentist can explain which option suits your teeth best.',
        ua: 'Часто так — при невеликих сколах, щілинах чи корекції форми. Лікар пояснить, який варіант найкраще підходить вашим зубам.',
        ru: 'Часто да — при небольших сколах, щелях или коррекции формы. Врач объяснит, какой вариант лучше подходит вашим зубам.',
        ro: 'Adesea da, pentru ciobituri mici, spații sau modificări de formă. Medicul vă poate explica ce opțiune se potrivește cel mai bine.',
      },
    },
    {
      q: { en: 'How do I care for veneers?', ua: 'Як доглядати за вінірами?', ru: 'Как ухаживать за винирами?', ro: 'Cum îngrijesc fațetele?' },
      a: {
        en: 'Brush twice a day, clean between teeth, attend regular check-ups and hygiene visits, and wear a night guard if you grind your teeth.',
        ua: 'Чистіть зуби двічі на день, очищайте міжзубні проміжки, регулярно відвідуйте огляди і гігієну, а при бруксизмі носіть нічну капу.',
        ru: 'Чистите зубы дважды в день, очищайте межзубные промежутки, регулярно посещайте осмотры и гигиену, а при бруксизме носите ночную капу.',
        ro: 'Periați-vă de două ori pe zi, curățați între dinți, mergeți regulat la controale și igienizări și purtați o gutieră de noapte dacă scrâșniți din dinți.',
      },
    },
  ],
  readAlso: [
    {
      to: '/veneers-london',
      label: { en: 'Veneers in London', ua: 'Вініри в Лондоні', ru: 'Виниры в Лондоне', ro: 'Fațete dentare în Londra' },
    },
    {
      to: '/services/smile-makeover',
      label: { en: 'Smile Makeover', ua: 'Зміна усмішки', ru: 'Изменение улыбки', ro: 'Smile Makeover' },
    },
  ],
}
